import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  GitBranch,
  Plus,
  Copy,
  Flag,
  Save,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  Users,
  CloudRain,
  FastForward,
  Zap,
  Settings,
  BarChart3,
  ArrowRight,
  X,
  ChevronRight,
  FileText,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { format } from 'date-fns';

// Icon mapping for template types
const TEMPLATE_ICONS = {
  'users-plus': Users,
  'users-minus': Users,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'calendar-plus': Calendar,
  'clock-plus': Clock,
  'fast-forward': FastForward,
  'cloud-rain': CloudRain,
  'git-branch': GitBranch,
};

// Category colors
const CATEGORY_COLORS = {
  resource: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  duration: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  calendar: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  logic: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  risk: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  custom: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
};

const ScenarioModePanel = ({
  isOpen,
  onClose,
  selectedScenarioId,
  onScenarioSelect,
  onScenarioCreated,
  onScenarioUpdated,
}) => {
  const { activeSchedule, can } = useProject();

  // State
  const [templates, setTemplates] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [applying, setApplying] = useState(false);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createMode, setCreateMode] = useState('template'); // 'template' | 'custom'
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [newScenario, setNewScenario] = useState({ name: '', description: '' });
  const [templateParameters, setTemplateParameters] = useState({});

  // Actions dialog state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [baselineName, setBaselineName] = useState('');
  const [comparisonData, setComparisonData] = useState(null);

  // Current tab
  const [activeTab, setActiveTab] = useState('scenarios');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get('/scenarios/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Fetch scenarios
  const fetchScenarios = useCallback(async () => {
    if (!activeSchedule?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/scenarios/schedule/${activeSchedule.id}`);
      setScenarios(response.data);
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);

  // Fetch scenario details
  const fetchScenarioDetails = useCallback(async (scenarioId) => {
    if (!scenarioId) {
      setSelectedScenario(null);
      setForecast(null);
      return;
    }

    try {
      const [scenarioRes, forecastRes] = await Promise.all([
        api.get(`/scenarios/${scenarioId}`),
        api.get(`/scenarios/${scenarioId}/forecast`)
      ]);
      setSelectedScenario(scenarioRes.data);
      setForecast(forecastRes.data);
    } catch (error) {
      console.error('Failed to fetch scenario details:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchScenarios();
    }
  }, [isOpen, fetchTemplates, fetchScenarios]);

  useEffect(() => {
    if (selectedScenarioId) {
      fetchScenarioDetails(selectedScenarioId);
    }
  }, [selectedScenarioId, fetchScenarioDetails]);

  // Initialize template parameters when template is selected
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        const defaults = {};
        template.parameters.forEach(p => {
          defaults[p.key] = p.default;
        });
        setTemplateParameters(defaults);
        setNewScenario(prev => ({
          ...prev,
          name: prev.name || template.name,
          description: prev.description || template.description
        }));
      }
    }
  }, [selectedTemplateId, templates]);

  // Create scenario from template
  const handleCreateFromTemplate = async () => {
    if (!activeSchedule?.id || !selectedTemplateId || !newScenario.name.trim()) return;

    setCreating(true);
    try {
      const response = await api.post('/scenarios/from-template', {
        schedule_id: activeSchedule.id,
        template_id: selectedTemplateId,
        name: newScenario.name.trim(),
        description: newScenario.description?.trim() || null,
        parameters: templateParameters
      });

      toast.success('Scenario created successfully');
      setShowCreateDialog(false);
      resetCreateForm();
      await fetchScenarios();
      onScenarioSelect?.(response.data.id);
      onScenarioCreated?.(response.data);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to create scenario');
    } finally {
      setCreating(false);
    }
  };

  // Create custom scenario
  const handleCreateCustom = async () => {
    if (!activeSchedule?.id || !newScenario.name.trim()) return;

    setCreating(true);
    try {
      const response = await api.post('/scenarios/custom', {
        schedule_id: activeSchedule.id,
        name: newScenario.name.trim(),
        description: newScenario.description?.trim() || null
      });

      toast.success('Custom scenario created');
      setShowCreateDialog(false);
      resetCreateForm();
      await fetchScenarios();
      onScenarioSelect?.(response.data.id);
      onScenarioCreated?.(response.data);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to create scenario');
    } finally {
      setCreating(false);
    }
  };

  // Apply parameters
  const handleApplyParameters = async () => {
    if (!selectedScenario?.id || !selectedScenario.template_id) return;

    setApplying(true);
    try {
      await api.post(`/scenarios/${selectedScenario.id}/apply-parameters`, {
        parameters: selectedScenario.parameters,
        recalculate_cpm: true
      });

      toast.success('Parameters applied and CPM recalculated');
      await fetchScenarioDetails(selectedScenario.id);
      onScenarioUpdated?.();
    } catch (error) {
      console.error('Failed to apply parameters:', error);
      toast.error('Failed to apply parameters');
    } finally {
      setApplying(false);
    }
  };

  // Duplicate scenario
  const handleDuplicate = async () => {
    if (!selectedScenario?.id || !duplicateName.trim()) return;

    setCreating(true);
    try {
      const response = await api.post(`/scenarios/${selectedScenario.id}/duplicate`, {
        new_name: duplicateName.trim()
      });

      toast.success('Scenario duplicated');
      setShowDuplicateDialog(false);
      setDuplicateName('');
      await fetchScenarios();
      onScenarioSelect?.(response.data.id);
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
      toast.error('Failed to duplicate scenario');
    } finally {
      setCreating(false);
    }
  };

  // Convert to baseline
  const handleConvertToBaseline = async () => {
    if (!selectedScenario?.id || !baselineName.trim()) return;

    setCreating(true);
    try {
      await api.post(`/scenarios/${selectedScenario.id}/convert-to-baseline`, {
        baseline_name: baselineName.trim(),
        baseline_type: 'what_if'
      });

      toast.success('Scenario converted to baseline');
      setShowConvertDialog(false);
      setBaselineName('');
      await fetchScenarios();
      await fetchScenarioDetails(selectedScenario.id);
    } catch (error) {
      console.error('Failed to convert to baseline:', error);
      toast.error('Failed to convert to baseline');
    } finally {
      setCreating(false);
    }
  };

  // Reset create form
  const resetCreateForm = () => {
    setNewScenario({ name: '', description: '' });
    setSelectedTemplateId(null);
    setTemplateParameters({});
    setCreateMode('template');
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Get template icon
  const getTemplateIcon = (iconName) => {
    const IconComponent = TEMPLATE_ICONS[iconName] || GitBranch;
    return IconComponent;
  };

  // Get category style
  const getCategoryStyle = (category) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.custom;
  };

  // Render parameter input
  const renderParameterInput = (param, value, onChange) => {
    switch (param.type) {
      case 'slider':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">{param.min}{param.unit}</span>
              <span className="font-medium">{value}{param.unit}</span>
              <span className="text-slate-500">{param.max}{param.unit}</span>
            </div>
            <Slider
              value={[value]}
              min={param.min}
              max={param.max}
              step={param.step}
              onValueChange={([v]) => onChange(v)}
              className="w-full"
            />
          </div>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            min={param.min}
            max={param.max}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="h-8"
          />
        );
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={value}
              onCheckedChange={onChange}
            />
            <span className="text-xs text-slate-500">{value ? 'Yes' : 'No'}</span>
          </div>
        );
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="h-8"
          />
        );
    }
  };

  // Render template card
  const renderTemplateCard = (template) => {
    const Icon = getTemplateIcon(template.icon);
    const style = getCategoryStyle(template.category);
    const isSelected = selectedTemplateId === template.id;

    return (
      <div
        key={template.id}
        className={`p-3 rounded-lg border cursor-pointer transition-all ${
          isSelected
            ? `${style.bg} ${style.border} ring-2 ring-offset-1 ring-amber-400`
            : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
        }`}
        onClick={() => setSelectedTemplateId(template.id)}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${style.bg}`}>
            <Icon className={`h-4 w-4 ${style.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-slate-900">{template.name}</span>
              <Badge variant="outline" className={`text-[9px] ${style.text} ${style.border}`}>
                {template.category}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{template.description}</p>
          </div>
          {isSelected && (
            <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[500px] sm:w-[600px] p-0">
          <SheetHeader className="p-4 border-b bg-amber-50">
            <SheetTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-amber-600" />
              Scenario Manager
            </SheetTitle>
            <SheetDescription>
              Create, edit, and compare what-if scenarios for your schedule
            </SheetDescription>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 p-0 h-10">
              <TabsTrigger
                value="scenarios"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-transparent"
              >
                <Layers className="h-4 w-4 mr-1" />
                My Scenarios
              </TabsTrigger>
              <TabsTrigger
                value="templates"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-transparent"
              >
                <FileText className="h-4 w-4 mr-1" />
                Templates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scenarios" className="m-0 flex-1">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="p-4 space-y-4">
                  {/* Create Button */}
                  {can(Action.MANAGE_SCHEDULES) && (
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      className="w-full h-10 bg-amber-500 hover:bg-amber-600 text-white gap-2"
                      data-testid="create-scenario-panel-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Create New Scenario
                    </Button>
                  )}

                  {/* Scenarios List */}
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : scenarios.length === 0 ? (
                    <div className="text-center py-12">
                      <GitBranch className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">No scenarios created yet</p>
                      <p className="text-xs text-slate-400 mt-1">Create a scenario to explore what-if analysis</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {scenarios.map((scenario) => (
                        <div
                          key={scenario.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedScenarioId === scenario.id
                              ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => {
                            onScenarioSelect?.(scenario.id);
                            fetchScenarioDetails(scenario.id);
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <GitBranch className="h-4 w-4 text-amber-500" />
                              <span className="font-medium text-sm">{scenario.name}</span>
                            </div>
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                scenario.status === 'draft' ? 'text-blue-600 border-blue-200' :
                                scenario.status === 'saved' ? 'text-green-600 border-green-200' :
                                scenario.status === 'applied' ? 'text-purple-600 border-purple-200' :
                                'text-slate-500 border-slate-200'
                              }`}
                            >
                              {scenario.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            {scenario.template_name && (
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {scenario.template_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(scenario.created_at)}
                            </span>
                            <span>{scenario.activity_count} activities</span>
                          </div>
                          {scenario.variance_vs_current_days !== null && (
                            <div className="mt-2 flex items-center gap-2">
                              <Badge
                                className={`text-[10px] ${
                                  scenario.variance_vs_current_days > 0
                                    ? 'bg-red-100 text-red-700'
                                    : scenario.variance_vs_current_days < 0
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {scenario.variance_vs_current_days > 0 ? '+' : ''}{scenario.variance_vs_current_days}d vs current
                              </Badge>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Scenario Details */}
                  {selectedScenario && (
                    <>
                      <Separator className="my-4" />
                      <div className="space-y-4">
                        <h4 className="font-medium text-sm text-slate-900">Scenario Details</h4>

                        {/* Forecast Summary */}
                        {forecast && (
                          <Card className="border-slate-200">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-xs font-medium text-slate-500 uppercase">Forecast Impact</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="p-2 bg-slate-50 rounded">
                                  <p className="text-[10px] text-slate-500">Current</p>
                                  <p className="text-xs font-medium">{formatDate(forecast.current_plan_finish_date)}</p>
                                </div>
                                <div className="p-2 bg-amber-50 rounded">
                                  <p className="text-[10px] text-amber-600">Scenario</p>
                                  <p className="text-xs font-medium text-amber-700">{formatDate(forecast.forecast_finish_date)}</p>
                                </div>
                                <div className="p-2 bg-purple-50 rounded">
                                  <p className="text-[10px] text-purple-600">Baseline</p>
                                  <p className="text-xs font-medium">{formatDate(forecast.baseline_finish_date)}</p>
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">Duration Impact</span>
                                <Badge
                                  className={`${
                                    forecast.variance_vs_current_days > 0
                                      ? 'bg-red-100 text-red-700'
                                      : forecast.variance_vs_current_days < 0
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {forecast.variance_vs_current_days > 0 ? '+' : ''}{forecast.variance_vs_current_days || 0} days
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">Activities Modified</span>
                                <span className="font-medium">{forecast.activities_changed}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">Critical Activities</span>
                                <span className="font-medium">{forecast.critical_activities}</span>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Parameters (if template-based) */}
                        {selectedScenario.template_id && selectedScenario.parameters && (
                          <Card className="border-slate-200">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-xs font-medium text-slate-500 uppercase flex items-center gap-2">
                                <Settings className="h-3 w-3" />
                                Parameters
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              {Object.entries(selectedScenario.parameters).map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between text-xs">
                                  <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                  <span className="font-medium">
                                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                  </span>
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        )}

                        {/* Actions */}
                        {can(Action.MANAGE_SCHEDULES) && selectedScenario.is_editable && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs gap-1"
                              onClick={() => {
                                setDuplicateName(`${selectedScenario.name} (Copy)`);
                                setShowDuplicateDialog(true);
                              }}
                            >
                              <Copy className="h-3 w-3" />
                              Duplicate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                              onClick={() => {
                                setBaselineName(`${selectedScenario.name} Baseline`);
                                setShowConvertDialog(true);
                              }}
                            >
                              <Flag className="h-3 w-3" />
                              Convert to Baseline
                            </Button>
                            {selectedScenario.template_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs gap-1"
                                onClick={handleApplyParameters}
                                disabled={applying}
                              >
                                {applying ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                                Recalculate
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="templates" className="m-0">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="p-4 space-y-4">
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <>
                      {/* Group templates by category */}
                      {['resource', 'duration', 'calendar', 'logic', 'risk'].map((category) => {
                        const categoryTemplates = templates.filter(t => t.category === category);
                        if (categoryTemplates.length === 0) return null;

                        const style = getCategoryStyle(category);
                        return (
                          <div key={category}>
                            <h4 className={`text-xs font-semibold uppercase mb-2 ${style.text}`}>
                              {category} Scenarios
                            </h4>
                            <div className="space-y-2">
                              {categoryTemplates.map(renderTemplateCard)}
                            </div>
                          </div>
                        );
                      })}

                      {/* Use selected template */}
                      {selectedTemplateId && can(Action.MANAGE_SCHEDULES) && (
                        <Button
                          onClick={() => setShowCreateDialog(true)}
                          className="w-full h-10 bg-amber-500 hover:bg-amber-600 text-white gap-2 mt-4"
                        >
                          <Plus className="h-4 w-4" />
                          Create Scenario from Selected Template
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-amber-500" />
              Create Scenario
            </DialogTitle>
            <DialogDescription>
              Create a what-if scenario to explore schedule changes safely
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            <Tabs value={createMode} onValueChange={setCreateMode} className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-4">
                <TabsTrigger value="template">From Template</TabsTrigger>
                <TabsTrigger value="custom">Custom Scenario</TabsTrigger>
              </TabsList>

              <TabsContent value="template" className="space-y-4">
                {/* Template Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select Template</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
                    {templates.map((template) => {
                      const Icon = getTemplateIcon(template.icon);
                      const style = getCategoryStyle(template.category);
                      const isSelected = selectedTemplateId === template.id;

                      return (
                        <div
                          key={template.id}
                          className={`p-2 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? `${style.bg} ${style.border} ring-1 ring-amber-400`
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setSelectedTemplateId(template.id)}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${style.text}`} />
                            <span className="text-xs font-medium truncate">{template.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Parameters */}
                {selectedTemplateId && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Parameters</Label>
                    {templates.find(t => t.id === selectedTemplateId)?.parameters.map((param) => (
                      <div key={param.key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-600">{param.label}</Label>
                          {param.unit && (
                            <span className="text-[10px] text-slate-400">{param.unit}</span>
                          )}
                        </div>
                        {renderParameterInput(
                          param,
                          templateParameters[param.key],
                          (value) => setTemplateParameters(prev => ({ ...prev, [param.key]: value }))
                        )}
                        {param.description && (
                          <p className="text-[10px] text-slate-400">{param.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="custom" className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Zap className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Create a blank scenario</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Edit activities and relationships manually
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <Separator className="my-4" />

            {/* Scenario Name & Description */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="scenario-name" className="text-sm font-medium">
                  Scenario Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="scenario-name"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Accelerated Schedule"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="scenario-desc" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="scenario-desc"
                  value={newScenario.description}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the scenario..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetCreateForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={createMode === 'template' ? handleCreateFromTemplate : handleCreateCustom}
              disabled={creating || !newScenario.name.trim() || (createMode === 'template' && !selectedTemplateId)}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Scenario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-slate-500" />
              Duplicate Scenario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label htmlFor="dup-name">New Scenario Name</Label>
              <Input
                id="dup-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Enter name for the copy"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} disabled={creating || !duplicateName.trim()}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Baseline Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-purple-500" />
              Convert to Baseline
            </DialogTitle>
            <DialogDescription>
              Create a baseline snapshot from this scenario&apos;s current state
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label htmlFor="bl-name">Baseline Name</Label>
              <Input
                id="bl-name"
                value={baselineName}
                onChange={(e) => setBaselineName(e.target.value)}
                placeholder="Enter baseline name"
              />
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              The scenario will be marked as &quot;Applied&quot; after conversion.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button
              onClick={handleConvertToBaseline}
              disabled={creating || !baselineName.trim()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Convert to Baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScenarioModePanel;
